/******************************************************************************
 *** MbrasChangeMng ** 조합원 변경 등록 관리
 *******************************************************************************/
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Button from "@/js/common/ui/Button";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import Modal from "@/js/common/ui/modal/Modal";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import Util from "@/js/common/Util";
import React from "react";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";

import DialogTitleBar from "../../common/DialogTitleBar";
import MbrasChangeSearchDialog from "./dialog/MbrasChangeSearchDialog";
import MbrasChangeTypeDialog from "./dialog/MbrasChangeTypeDialog";
import MbrasRegulApply from "./change/MbrasRegulApply";
import MbrasDepartApply from "./change/MbrasDepartApply";
import MbrasLeaveApply from "./change/MbrasLeaveApply";
import MbrasLeaveExtendApply from "./change/MbrasLeaveExtendApply";
import MbrasReturnApply from "./change/MbrasReturnApply";
import MbrasResignApply from "./change/MbrasResignApply";
import MbrasRetireApply from "./change/MbrasRetireApply";
import MbrasRejoinApply from "./change/MbrasRejoinApply";

type ModalStep =
    | "MEMBER_SEARCH"
    | "CHANGE_TYPE"
    | "REGUL"
    | "DEPART"
    | "LEAVE"
    | "LEAVE_EXTEND"
    | "RETURN"
    | "RESIGN"
    | "RETIRE"
    | "REJOIN";


const MbrasChangeMng = () => {
    const userInfo = useRecoilValue(Atom.userInfo);

    const [keyword          , setKeyword          ] = React.useState("");                            // 성명 검색어
    const [searchAtrzSttsCd , setSearchAtrzSttsCd ] = React.useState("");                            // 조합원 상태 검색조건
    const [upOgnzCd         , setUpOgnzCd         ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd           , setOgnzCd           ] = React.useState<string>(userInfo.ognzCd.includes("000") ? "" : userInfo.ognzCd); // 조직코드(지부)
    const [ocptTypeCd       , setOcptTypeCd       ] = React.useState("");                            // 직종 검색조건
    const [sttDt            , setSttDt            ] = React.useState("");                            // 작성일자 시작일
    const [endDt            , setEndDt            ] = React.useState("");                            // 작성일자 종료일
    const [dataArr          , setDataArr          ] = React.useState<any[]>([]);                     // 변경 신청 문서 목록
    const [modalOpen        , setModalOpen        ] = React.useState(false);                         // 변경등록 모달 표시 여부
    const [modalStep        , setModalStep        ] = React.useState<ModalStep>("MEMBER_SEARCH");    // 변경등록 모달 진행 단계
    const [callTime         , setCallTime         ] = React.useState<Date>(new Date());              // 목록 재조회 호출 시각
    const [selectedMember   , setSelectedMember   ] = React.useState<any>(null);                     // 선택한 조합원 정보
    const [mbrasId          , setMbrasId          ] = React.useState<string>("");                    // 조합원 ID
    const [mbrasAtrzDocId   , setMbrasAtrzDocId   ] = React.useState<string>("");                    // 조합원 결재 문서 ID
    const [_mbrasAtrzSeCd   , setMbrasAtrzSeCd    ] = React.useState<string>("");                    // 조합원 결재 구분코드

    const fldInfs: fldInfsProps[] = [
        { type: "seq" ,                            width:  50, headAlign: "center", dataAlign: "center", label: "No."         },
        { type: "text", fldNm: "mbrasId"         , width: 140, headAlign: "center", dataAlign: "center", label: "조합원 번호" },
        { type: "code", fldNm: "mbrasAtrzSeCd"   , width: 110, headAlign: "center", dataAlign: "center", label: "변경 구분", catCode: "MBRAS_ATRZ_SE_CD" },
        { type: "text", fldNm: "mbrasNm"         , width:  90, headAlign: "center", dataAlign: "center", label: "성명"         },
        { type: "code", fldNm: "atrzSttsCd"      , width:  90, headAlign: "center", dataAlign: "center", label: "상태", catCode: "UI_ATRZ_STTS_CD_01" },
        { type: "text", fldNm: "upOgnzNm"        , width: 120, headAlign: "center", dataAlign: "center", label: "본부"         },
        { type: "text", fldNm: "ognzNm"          , width: 120, headAlign: "center", dataAlign: "center", label: "지부"         },
        { type: "text", fldNm: "wtjrNm"          , width: 110, headAlign: "center", dataAlign: "center", label: "관내"         },
        { type: "code", fldNm: "ocptTypeCd"      , width: 100, headAlign: "center", dataAlign: "center", label: "직종", catCode: "OCPT_TYPE_CD" },
        { type: "text", fldNm: "crtDt"           , width: 120, headAlign: "center", dataAlign: "center", label: "작성일자"     },
    ];

    React.useEffect(() => {
        if (Util.isNotEmpty(sttDt) && Util.isNotEmpty(endDt) && sttDt > endDt) {
            AppContext.openAlert({
                title: "작성일자 검색 조건 확인",
                msg  : "작성일자의 검색 시작일자가 종료일자보다 느립니다.",
                icon : "warning",
            });
            setEndDt("");
            return;
        }

        AppContext.showSpinner();

        Axios.dataAccess({
            url       : "system/mbras/chg/select-mbras-atrz-chg-list.do",
            methodType: "post",
            paramType : "json",
            paramData : {
                searchAtrzSttsCd: searchAtrzSttsCd,
                upOgnzCd        : upOgnzCd === "00" ? "" : upOgnzCd,
                ognzCd          : ognzCd === "00_000" ? "" : ognzCd,
                ocptTypeCd      : ocptTypeCd,
                sttDt           : sttDt,
                endDt           : endDt,
            },
            onSuccessFn: (res: any) => {
                setDataArr(res.result.list);
                AppContext.hideSpinner();
            },
        });
    }, [callTime, searchAtrzSttsCd, upOgnzCd, ognzCd, ocptTypeCd, sttDt, endDt]);

    const resetModalInfo = () => {
        setModalStep("MEMBER_SEARCH");
        setSelectedMember(null);
        setMbrasId("");
        setMbrasAtrzDocId("");
        setMbrasAtrzSeCd("");
    };

    const closeModal = () => {
        resetModalInfo();
        setModalOpen(false);
    };

    const openNewDocument = () => {
        resetModalInfo();
        setModalOpen(true);
    };

    const selectMember = (mbrasId: string) => {
        AppContext.showSpinner();

        Axios.dataAccess({
            url       : "system/mbras/chg/select-mbras-chg-detail.do",
            methodType: "post",
            paramType : "json",
            paramData : {
                mbrasId: mbrasId,
            },
            onSuccessFn: (res: any) => {
                setMbrasId(mbrasId);
                setSelectedMember(res.result.item);
                setModalStep("CHANGE_TYPE");

                AppContext.hideSpinner();
            },
        });
    };

    const getApplyStep = (changeCd: string): ModalStep | null => {
        switch (changeCd) {
            case "04": return "REGUL";
            case "05": return "DEPART";
            case "06": return "LEAVE";
            case "07": return "LEAVE_EXTEND";
            case "08": return "RETURN";
            case "09": return "RESIGN";
            case "10": return "RETIRE";
            case "11": return "REJOIN";
            default  : return null;
        }
    };

    const openSavedDocument = (item: any) => {
        const step = getApplyStep(String(item.mbrasAtrzSeCd));

        if (step === null) {
            AppContext.openAlert({
                title: "변경 구분 확인",
                msg  : "지원하지 않는 변경 신청 문서입니다.",
                icon : "warning",
            });
            return;
        }

        AppContext.showSpinner();

        Axios.dataAccess({
            url       : "system/mbras/chg/select-mbras-chg-detail.do",
            methodType: "post",
            paramType : "json",
            paramData : {
                mbrasId: item.mbrasId,
            },
            onSuccessFn: (res: any) => {
                setSelectedMember(res.result.item);
                setMbrasId(String(item.mbrasId));
                setMbrasAtrzDocId(String(item.mbrasAtrzDocId));
                setMbrasAtrzSeCd(String(item.mbrasAtrzSeCd));
                setModalStep(step);
                setModalOpen(true);

                AppContext.hideSpinner();
            },
        });
    };

    const getModalTitle = () => {
        switch (modalStep) {
            case "MEMBER_SEARCH": return "변경 등록 조합원 조회";
            case "CHANGE_TYPE"  : return "변경 구분 선택";
            case "REGUL"        : return Util.isEmpty(mbrasAtrzDocId) ? "정규직 전환 등록" : "정규직 전환 수정";
            case "DEPART"       : return Util.isEmpty(mbrasAtrzDocId) ? "소속변경 등록" : "소속변경 수정";
            case "LEAVE"        : return Util.isEmpty(mbrasAtrzDocId) ? "휴직 신청 등록" : "휴직 신청 수정";
            case "LEAVE_EXTEND" : return Util.isEmpty(mbrasAtrzDocId) ? "휴직 연장신청 등록" : "휴직 연장신청 수정";
            case "RETURN"       : return Util.isEmpty(mbrasAtrzDocId) ? "복직 신청 등록" : "복직 신청 수정";
            case "RESIGN"       : return Util.isEmpty(mbrasAtrzDocId) ? "의원면직 등록" : "의원면직 수정";
            case "RETIRE"       : return Util.isEmpty(mbrasAtrzDocId) ? "퇴직 등록" : "퇴직 수정";
            case "REJOIN"       : return Util.isEmpty(mbrasAtrzDocId) ? "재가입 등록" : "재가입 수정";
            default             : return "조합원 변경 등록";
        }
    };

    const renderModalContents = () => {
        switch (modalStep) {
            case "MEMBER_SEARCH":
                return (
                    <MbrasChangeSearchDialog
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        selectMember={selectMember}                 // 선택한 조합원 상세조회 함수
                    />
                );

            case "CHANGE_TYPE":
                return (
                    <MbrasChangeTypeDialog
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setMbrasAtrzSeCd={setMbrasAtrzSeCd}         // 조합원 결재 구분코드 설정 함수
                    />
                );

            case "REGUL":
                return (
                    <MbrasRegulApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            case "DEPART":
                return (
                    <MbrasDepartApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            case "LEAVE":
                return (
                    <MbrasLeaveApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            case "LEAVE_EXTEND":
                return (
                    <MbrasLeaveExtendApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            case "RETURN":
                return (
                    <MbrasReturnApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            case "RESIGN":
                return (
                    <MbrasResignApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            case "RETIRE":
                return (
                    <MbrasRetireApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            case "REJOIN":
                return (
                    <MbrasRejoinApply
                        mbrasId={mbrasId}                           // 조합원 ID
                        mbrasAtrzDocId={mbrasAtrzDocId}             // 조합원 결재 문서 ID
                        selectedMember={selectedMember}             // 선택한 조합원 정보
                        setModalOpen={setModalOpen}                 // 모달 표시 여부 변경 함수
                        setModalStep={setModalStep}                 // 모달 진행 단계 변경 함수
                        setCallTime={setCallTime}                   // 목록 재조회 호출 시각 변경 함수
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            <div className="mt5">
                <span className="posR dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">조합원 변경 등록</span>
                <div className="bdt3 bdc007bff pb10"></div>
            </div>

            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC gap14 flxWrap">
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">성명</span>
                        <TextBox
                            value={keyword}
                            width={120}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            style={{ backgroundColor: "#fff", padding: "0 10px" }}
                            placeholder="성명 입력"
                            onChangeFunc={(text: string) => setKeyword(text)}
                        />
                    </div>

                    {/* 1차 소속(본부) */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox
                            value={upOgnzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background={userInfo.ognzCd === "00_000" ? "#fff" : "#F6F6F6"}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={userInfo.ognzCd === "00_000" ? false : true}
                            onChangeFunc={(data: string) => {
                                setUpOgnzCd(data);
                                if (data === "00") {
                                    setOgnzCd("00_000");
                                } else {
                                    setOgnzCd("");
                                }
                            }}
                        />
                    </div>

                    {/* 2차 소속(지부) */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox
                            value={ognzCd}
                            width={180}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background={userInfo.ognzCd.includes("000") ? "#fff" : "#F6F6F6"}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getDownOrgCodeArray(upOgnzCd))}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={!userInfo.ognzCd.includes("000")}
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">상태</span>
                        <SelectBox
                            value={searchAtrzSttsCd}
                            width={100}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={[{code: "", name: "전체"},].concat(Util.getCodeArray("UI_ATRZ_STTS_CD_01"))}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">직종</span>
                        <SelectBox
                            value={ocptTypeCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("OCPT_TYPE_CD"))}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setOcptTypeCd(data)}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w70 fm-c fs15 fc333333 ls1 taL">작성일자</span>
                        <DatePickerBox
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            bgColor="white"
                            textAlign="left"
                            calendarThemeColor={AppContext.themeColor}
                            className="mr5"
                            value={sttDt}
                            onChange={(data: string) => setSttDt(data)}
                        />
                        <span className="fm-c fc666666 mr5">~</span>
                        <DatePickerBox
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            bgColor="white"
                            textAlign="left"
                            calendarThemeColor={AppContext.themeColor}
                            value={endDt}
                            onChange={(data: string) => setEndDt(data)}
                        />
                    </div>

                    <div className="dpFlx aiC mlA">
                        <Button
                            width={75}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styInitBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="초기화"
                            onClickFunc={() => {
                                setKeyword("");
                                setSearchAtrzSttsCd("");
                                setUpOgnzCd(userInfo.upOgnzCd);
                                setOgnzCd(userInfo.ognzCd.includes("000") ? "" : userInfo.ognzCd);
                                setOcptTypeCd("");
                                setSttDt("");
                                setEndDt("");
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="dpFlx aiC jcSb mb10 h44">
                <div className="fm-b fs16 fc007bff ls1 fw700">
                    <span>변경 신청 문서</span>
                </div>

                <Button
                    width={110}
                    height={40}
                    fontFamily="fm-b"
                    fontSize={16}
                    letterSpacing={-1}
                    variant="styCompleteBtn"
                    borderRadius={6}
                    buttonClass="dpFlx aiC jcC"
                    buttonName="변경 등록"
                    onClickFunc={openNewDocument}
                />
            </div>

            <GridTable
                tableInfo={{
                    tableHeadVsb : true,
                    styleType    : AdminGridStyle,
                    fldInfs      : fldInfs,
                    ordFld       : { name: "crtDt", direction: "desc" },
                    dataArr      : Util.filterFieldData(dataArr, fldInfs, "mbrasNm", keyword),
                    maxRowNums   : [10, 20, 30],
                    theadTrHeight: 40,
                    tbodyTrHeight: 35,
                    paginateClass: "pt10",
                    paginateStyle: {
                        buttonOnColor    : AppContext.boardThemeColor,
                        buttonWidth      : 22,
                        buttonHeight     : 24,
                        buttonMarginLeft : 1,
                        buttonMarginRight: 1,
                        fontSize         : 13,
                        color            : "#666666",
                        letterSpacing    : -0.8,
                        fontFamily       : "Pretendard-Regular",
                        borderRadius     : 3,
                    },
                    footerVsb   : true,
                    rowClickFunc: (item: any) => openSavedDocument(item),
                }}
            />

            <Modal modalOpen={modalOpen} dim={true} visible={true}
                titleEl={<DialogTitleBar title={getModalTitle()} backgroundColor={AppContext.themeColor} />}
                onCloseFunc={closeModal}
            >
                {renderModalContents()}
            </Modal>
        </div>
    );
};

export default MbrasChangeMng;